"use client";

// src/components/MiniFooter.tsx
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { content } from '../content';
import { supabaseAnonKey, supabaseUrl } from '@/lib/supabaseConfig';
import { IoLogoYoutube, IoLogoInstagram, IoMailOutline, IoCallOutline } from 'react-icons/io5';

const MiniFooter = () => {
  const { footerLogoUrl, footer } = content;
  const [socialHandles, setSocialHandles] = useState({
    instagram_url: 'https://www.instagram.com/astrojiofficial',
    youtube_url: 'https://www.youtube.com/@astrolearn_app',
    whatsapp_group_url: 'https://chat.whatsapp.com/KTu3RlGXcx53WnjDunoRoF?mode=wwt',
    support_phone: '7973938627',
    support_email: 'support@thefuture.university'
  });

  useEffect(() => {
    const fetchSocialHandles = async () => {
      try {
        const response = await fetch(`${supabaseUrl}/rest/v1/website_social_handles?select=*&id=eq.default`, {
          headers: {
            Accept: 'application/json',
            apikey: supabaseAnonKey,
            Authorization: `Bearer ${supabaseAnonKey}`,
          },
          cache: 'force-cache',
          priority: 'low',
        });
        if (!response.ok) return;
        const [data] = await response.json();

        if (data) {
          setSocialHandles({
            instagram_url: data.instagram_url || 'https://www.instagram.com/astrojiofficial',
            youtube_url: data.youtube_url || 'https://www.youtube.com/@astrolearn_app',
            whatsapp_group_url: data.whatsapp_group_url || 'https://chat.whatsapp.com/KTu3RlGXcx53WnjDunoRoF?mode=wwt',
            support_phone: data.support_phone || '7973938627',
            support_email: data.support_email || 'support@thefuture.university'
          });
        }
      } catch (err) {
        console.error('Error fetching social handles in MiniFooter:', err);
      }
    };

    let requested = false;
    const requestHandles = () => {
      if (requested) return;
      requested = true;
      void fetchSocialHandles();
    };
    const fallback = window.setTimeout(requestHandles, 30_000);
    window.addEventListener('pointerdown', requestHandles, { once: true, passive: true });
    window.addEventListener('keydown', requestHandles, { once: true });

    return () => {
      window.clearTimeout(fallback);
      window.removeEventListener('pointerdown', requestHandles);
      window.removeEventListener('keydown', requestHandles);
    };
  }, []);

  return (
    <footer className="bg-accent text-white pt-16 pb-8 mt-auto border-t border-white/10">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-8 mb-16">
          <div className="space-y-2">
            <Link prefetch={false} href="/" className="inline-block">
              <Image
                src={footerLogoUrl}
                alt="Astroji Logo"
                width={144}
                height={40}
                className="w-36 transition-opacity hover:opacity-90 h-auto"
              />
            </Link>
            <p className="text-gray-300 text-m font-semibold leading-relaxed max-w-xs">
              {footer.tagline}
            </p>
            <div className="flex items-center gap-4">
              <a href={socialHandles.youtube_url} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-primary hover:text-white transition-all duration-300">
                <IoLogoYoutube size={20} />
              </a>
              <a href={socialHandles.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-gradient-to-br hover:from-purple-600 hover:via-pink-500 hover:to-orange-400 transition-all duration-300">
                <IoLogoInstagram size={20} />
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-serif font-bold text-white mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link prefetch={false} href="/" className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/20 group-hover:bg-primary transition-colors"></span>
                  Home
                </Link>
              </li>
              <li>
                <Link prefetch={false} href="/consultations" className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/20 group-hover:bg-primary transition-colors"></span>
                  Consultations
                </Link>
              </li>
              <li>
                <Link prefetch={false} href="/rudraksha" className="text-gray-300 hover:text-amber-400 font-medium transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 group-hover:bg-amber-300 transition-colors"></span>
                  Certified Rudraksha
                </Link>
              </li>
              <li>
                <Link prefetch={false} href="/products" className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/20 group-hover:bg-primary transition-colors"></span>
                  Astro Mall
                </Link>
              </li>
              <li>
                <Link prefetch={false} href="/contact" className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/20 group-hover:bg-primary transition-colors"></span>
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-serif font-bold text-white mb-6">Connect With Us</h3>
            <div className="mt-8 space-y-3">
              <a href={`mailto:${socialHandles.support_email}`} className="flex items-center gap-3 text-sm text-gray-300 hover:text-white transition-colors">
                <IoMailOutline className="text-white text-lg" />
                <span>{socialHandles.support_email}</span>
              </a>
              <a href={`tel:${socialHandles.support_phone}`} className="flex items-center gap-3 text-sm text-gray-300 hover:text-white transition-colors">
                <IoCallOutline className="text-white text-lg" />
                <span>{socialHandles.support_phone}</span>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-gray-400">
            {footer.copyrightText}
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            {footer.links.map((link) => (
              <Link
                key={link.text}
                prefetch={false}
                href={link.href}
                className="text-xs text-gray-400 hover:text-primary transition-colors"
              >
                {link.text}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default MiniFooter;
